import * as React from 'react';
import { useContext } from 'react';
import { Cell, CellValue, ColumnInstance, Row as RTRow, TableInstance } from 'react-table';
import { INote } from './FileNotes';
import { Button, Col, Row, CustomInput } from 'reactstrap';
import { IUser } from '../Users/UsersTable';
import { UsersContext } from '../../contexts/UsersContext';
import { useHistory } from 'react-router-dom';
import { getJwtFromStorage } from '../../services/AuthenticationService';
import { formatDateTime } from '../../formatters/DateTimeFormatter';
import { sanitizeHTMLConfig } from '../../contexts/DomainContext';
import { IDocument } from './Documents/Documents';
const DOMPurify = require('dompurify');

export type IFileNoteCell =
  | (TableInstance<INote> & {
      column: ColumnInstance<INote>;
      row: RTRow<INote>;
      cell: Cell<INote, string>;
      value: CellValue<string>;
    } & { children?: React.ReactNode })
  | (TableInstance<INote> & {
      column: ColumnInstance<INote>;
      row: RTRow<INote>;
      cell: Cell<INote, number>;
      value: CellValue<number>;
    } & { children?: React.ReactNode })
  | (TableInstance<INote> & {
      column: ColumnInstance<INote>;
      row: RTRow<INote>;
      cell: Cell<INote, any>;
      value: CellValue<any>;
    } & { children?: React.ReactNode });

interface INoteCellProps {
  cell: IFileNoteCell;
  printNote?: () => void;
  selectedCaseNotes?: string[];
  setSelectedCaseNotes?: (noteIds: any) => any;
}

export const FileNoteMetadataCell: React.FC<INoteCellProps> = ({ cell }) => {
  const usersContext = useContext(UsersContext);
  const users = usersContext.users;

  let userName = '';

  if (cell && cell.row.original && cell.row.original.dateCreated) {
    const userCreated = cell.row.original.userCreated;
    if (userCreated) {
      const user = users.find((l: IUser) => l.userId === userCreated);
      userName = user?.lastName.toUpperCase() + ', ' + user?.firstName;
    }
    return (
      <header className={'text-right'}>
        {userName}
        <div className={'text-muted small'}>{formatDateTime(new Date(cell.row.original.dateCreated))}</div>
      </header>
    );
  } else {
    return <></>;
  }
};

export const FileNoteTextCell: React.FC<INoteCellProps> = ({ cell, printNote }) => {
  const history = useHistory();

  const { fileId, noteId, clientId, clientFirstName, clientLastName, documents, text, userCanEdit } = cell.row.original;

  const editNote = !!fileId;

  const isClientNote = Boolean(clientId && clientLastName && clientFirstName);

  const editFileNote = () => {
    if (fileId) {
      history.push('/files/' + fileId + '/notes/' + noteId);
    }
  };

  const goToClientProfile = () => {
    history.push('/clients/' + clientId);
  };

  const goToClientNotes = () => {
    history.push('/clients/' + clientId + '/notes');
  };

  const downloadDocument = (uploadId: string) => {
    const getUrl = (uId: string) => '/api/uploads/download/' + uId + '/' + getJwtFromStorage();
    const link = document.createElement('a');
    console.log(getUrl(uploadId));
    link.href = getUrl(uploadId);
    link.setAttribute('download', 'download');
    document.body.appendChild(link);
    link.click();
  };

  if (cell && cell.row.original) {
    return (
      <Row className={'mr-0'}>
        <Col md={9} className={'mb-3 mb-md-0'}>
          {isClientNote && clientLastName && clientFirstName && (
            <p className={'d-inline-flex align-items-baseline px-2 bg-primary-medium rounded'}>
              <span className={'small'}>Client Case Note for</span>
              <Button size={'sm'} color={'link'} className={'p-0 ml-1 font-weight-bold'} onClick={goToClientProfile}>
                {clientLastName.toUpperCase() + ', ' + clientFirstName}
              </Button>
            </p>
          )}
          <main
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(text, sanitizeHTMLConfig),
            }}
          />
          <footer className={'d-flex'} style={{ gap: 8 }}>
            {printNote && (
              <Button size={'sm'} color={'link'} className={'pl-0'} onClick={printNote}>
                Print
              </Button>
            )}
            {editNote ? (
              <Button size={'sm'} color={'link'} className={'p-0'} onClick={() => editFileNote()}>
                {userCanEdit ? 'Edit' : 'View'}
              </Button>
            ) : (
              <Button size={'sm'} color={'link'} className={'p-0'} onClick={() => goToClientNotes()}>
                View Client&apos;s Case Notes
              </Button>
            )}
          </footer>
        </Col>
        <Col md={3}>
          {documents && documents.length > 0 && (
            <div>
              <h6>Documents</h6>
              <ul>
                {documents.map((d: IDocument) => (
                  <li key={d.documentId}>
                    <Button
                      color={'link'}
                      className={'p-0'}
                      onClick={() => downloadDocument(d.uploadId)}
                      disabled={!d.uploadId}
                      style={{ wordBreak: 'break-all', textAlign: 'left' }}
                      title={'Download "' + d.fileName + '"'}
                    >
                      {d.fileName}
                    </Button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Col>
      </Row>
    );
  } else {
    return <></>;
  }
};

export const FileCheckboxCell: React.FC<INoteCellProps> = ({ cell, selectedCaseNotes, setSelectedCaseNotes }) => {
  const { noteId } = cell.row.original;
  return (
    <article>
      <CustomInput
        key={'print-select-' + noteId}
        type='checkbox'
        name={'isSelected'}
        id={'print-select-' + noteId}
        label={''}
        aria-label={'Select Note'}
        checked={selectedCaseNotes?.includes(noteId)}
        onClick={() => {
          if (selectedCaseNotes && setSelectedCaseNotes) {
            setSelectedCaseNotes((cn: any) =>
              selectedCaseNotes.includes(noteId)
                ? cn.filter((n: any) => n !== noteId)
                : cn
                ? cn.concat([noteId])
                : [noteId]
            );
          }
        }}
      />
    </article>
  );
};
